/**
 * The `1987 HUD` (08 §2.7, 01 §9): everything in the original's single top-left box - weapon
 * silhouette, 2-digit ammunition, 3-digit clock in seconds, a vertical fatigue bar and a vertical
 * 3-segment wound bar, the box turning white while he runs. The remake adds the objective line
 * and, because a lethal number is never hidden (08 §5), the bleed-out clock of a downed ranger.
 */

import './retroPanel.css';
import { TICK_RATE } from '../../sim/types';
import type { MissionDef, WeaponId, World } from '../../sim/types';
import { clamp, el, mmss, Shown } from './dom';
import { ammoFigure, WEAPON_SLOTS } from './weaponSlots';

const SVG_NS = 'http://www.w3.org/2000/svg';
const FATIGUE_H = 22;

/** Silhouettes on a 24 x 10 grid, filled with the text colour. */
const SILHOUETTES: Readonly<Record<WeaponId, string>> = {
  carbine: 'M0 3h5v-1h9v1h10v1h-10v1h-2v4h-2v-4h-3v3h-2v-3h-5z',
  grenade: 'M9 3h2v-2h3v2h2v1h1v4h-1v1h-6v-1h-1v-4h1z',
  law: 'M0 3h2v1h20v-1h2v5h-2v-1h-20v1h-2z',
  bomb: 'M6 2h12v7h-12zM11 0h2v2h-2z',
  knife: 'M0 5l12-2v4zM12 2h1v6h-1zM13 4h8v2h-8z',
  pistol: 'M4 2h14v3h-9v1h-2v3h-3z',
  smoke: 'M9 1h6v1h1v7h-8v-7h1z',
};

export class RetroPanel {
  readonly el: HTMLElement;
  private readonly shape: SVGElement;
  private readonly name: HTMLElement;
  private readonly ammo: HTMLElement;
  private readonly clock: HTMLElement;
  private readonly fatigue: HTMLElement;
  private readonly segs: HTMLElement[];
  private readonly line: HTMLElement;
  private readonly shown = new Shown();

  constructor(private readonly def: MissionDef) {
    this.el = el('div', 'hud-panel hud-87');
    const icon = document.createElementNS(SVG_NS, 'svg');
    icon.setAttribute('class', 'hud-87-icon');
    icon.setAttribute('viewBox', '0 0 24 10');
    this.shape = document.createElementNS(SVG_NS, 'path');
    icon.append(this.shape);
    this.name = el('span', 'hud-87-name');
    const weapon = el('div', 'hud-87-weapon');
    weapon.append(icon, this.name);

    this.ammo = el('span', 'hud-87-digits', '00');
    this.clock = el('span', 'hud-87-digits hud-87-clock', '000');

    const fatigueTrack = el('div', 'hud-87-bar');
    fatigueTrack.style.height = `${FATIGUE_H + 2}px`;
    this.fatigue = el('div', 'hud-87-fatigue');
    fatigueTrack.append(this.fatigue);
    const wounds = el('div', 'hud-87-wounds');
    this.segs = [el('span', 'hud-87-seg'), el('span', 'hud-87-seg'), el('span', 'hud-87-seg')];
    wounds.append(...this.segs);

    const top = el('div', 'hud-87-row');
    top.append(weapon, this.ammo, this.clock, fatigueTrack, wounds);
    this.line = el('div', 'hud-87-line');
    this.el.append(top, this.line);
  }

  update(world: World): void {
    const shown = this.shown;
    const ranger = world.ranger;

    if (shown.changed('weapon', ranger.weapon)) {
      this.shape.setAttribute('d', SILHOUETTES[ranger.weapon]);
      this.name.textContent = WEAPON_SLOTS.find((s) => s.weapon === ranger.weapon)?.short ?? '';
    }
    const ammo = ammoFigure(ranger.weapon, ranger.inventory, ranger.gear);
    if (shown.changed('ammo', ammo)) this.ammo.textContent = ammo;

    const seconds = clamp(Math.ceil(world.clockTicks / TICK_RATE), 0, 999);
    if (shown.changed('clock', seconds)) this.clock.textContent = String(seconds).padStart(3, '0');
    if (shown.changed('clockLow', seconds < 60)) this.clock.classList.toggle('hud-87-low', seconds < 60);

    const fatigue = clamp(ranger.fatigue, 0, 1);
    const px = Math.round(fatigue * FATIGUE_H);
    if (shown.changed('fatigue', px)) this.fatigue.style.height = `${px}px`;
    const tier = fatigue >= 0.8 ? 2 : fatigue >= 0.5 ? 1 : 0;
    if (shown.changed('tier', tier)) {
      this.fatigue.classList.toggle('hud-fatigue-1', tier === 1);
      this.fatigue.classList.toggle('hud-fatigue-2', tier === 2);
    }

    const downed = ranger.status === 'downed';
    const wounds = downed ? 3 : clamp(Math.floor(ranger.wounds), 0, 3);
    // The bar fills from the bottom, as the original's did.
    if (shown.changed('wounds', wounds)) {
      this.segs.forEach((seg, i) => seg.classList.toggle('hud-87-seg-on', 2 - i < wounds));
    }

    // Black while walking, white while running (01 §9).
    if (shown.changed('run', ranger.running)) this.el.classList.toggle('hud-87-run', ranger.running);

    const bleed = downed ? Math.max(0, Math.ceil(ranger.bleedTicks / TICK_RATE)) : -1;
    const done = world.objective.complete && !world.objective.failed;
    const text = downed
      ? `DOWN ${mmss(bleed)}`
      : `${this.def.objective.label} [${world.targetsDestroyed}/${world.targetsTotal}]`;
    if (shown.changed('line', text)) this.line.textContent = text;
    if (shown.changed('lineMode', downed ? 2 : done ? 1 : 0)) {
      this.line.classList.toggle('hud-87-down', downed);
      this.line.classList.toggle('hud-87-done', !downed && done);
    }
  }
}
